import { orgSelectors } from 'ducks/orgs'
import type { DistributorDataTypeV2 } from 'elements/hardwareSelectorV2/types'
import { ComponentTypesV2 } from 'opensolar-common'
import { AddIcon, Button, styled, TextField, Typography, useTheme } from 'opensolar-ui'
import useProjectOrderPresenter from 'pages/ordering/ProjectOrderPresenter/useProjectOrderPresenter'
import HardwareSearchInputV3 from 'pages/ordering/search/HardwareSearchInputV3'
import { HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { getLineItemProductDisplayName, isManualSource } from 'pages/ordering/utils'
import AddItemDialog, { BomComponentSelectorType } from 'projectSections/sections/hardware/bom/AddItemDialog'
import ComponentSelectorDialog from 'projectSections/sections/hardware/bom/ComponentSelectorDialog'
import { useTranslate } from 'ra-core'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import SourceSelector from './SourceSelector'
import type { LineItemWithResolvedSource } from './useOrderByProjectCartData'

const TableContainer = styled('div')(({ theme }) => ({
  marginBottom: 24,
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: 8,
  overflow: 'hidden',
}))

const TableHeader = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  padding: '12px 16px',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
}))

const HeaderTitle = styled('h3')({
  fontSize: 16,
  fontWeight: 600,
  margin: 0,
})

const HeaderSubtitle = styled('p')(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.grey[600],
  margin: 0,
}))

const StyledTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
})

const StyledTableHead = styled('thead')(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  '& th': {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.grey[700],
    textTransform: 'uppercase',
  },
}))

const StyledTableRow = styled('tr')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}))

const StyledTableCell = styled('td')(({ theme }) => ({
  padding: '10px 14px',
  fontSize: 12,
  color: theme.palette.grey[900],
  verticalAlign: 'middle',
}))

const ProductCell = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
})

const ProductImage = styled('img')({
  width: 48,
  height: 48,
  objectFit: 'contain',
  borderRadius: 4,
  border: '1px solid #e0e0e0',
})

const ProductImagePlaceholder = styled('div')(({ theme }) => ({
  width: 48,
  height: 48,
  backgroundColor: theme.palette.grey[200],
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 9,
  color: theme.palette.grey[500],
}))

const ProductInfo = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
})

const ProductDescription = styled('span')({
  fontSize: 12,
  fontWeight: 500,
})

const ProductCode = styled('span')(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.grey[600],
}))

const QuantityInput = styled(TextField)({
  width: 80,
  '& input': {
    padding: '6px 10px',
    fontSize: 12,
  },
})

const SourceSelectorCell = styled('div')({
  fontSize: 12,
  '& .MuiSelect-root, & .MuiSelect-select': { fontSize: 'inherit' },
})

const TableFooter = styled('div')(({ theme }) => ({
  padding: '12px 16px',
  borderTop: `1px solid ${theme.palette.grey[200]}`,
}))

const StyledAddItemButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.palette.grey[100],
  color: theme.palette.grey[900],
  border: `1px solid ${theme.palette.grey[300]}`,
  gap: 8,
}))

const SearchInputContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
})

type ShopItemsTableRowProps = {
  lineItem: LineItemWithResolvedSource
  onQuantityChange: (uuid: string, quantity: number) => void
  onSourceChange: (uuid: string, source: HardwareSupplierFilterKeyType | undefined) => void
}

const ShopItemsTableRow = memo(({ lineItem, onQuantityChange, onSourceChange }: ShopItemsTableRowProps) => {
  const currencySymbol = useSelector(orgSelectors.getCurrencySymbolFromOrg)

  const [quantity, setQuantity] = useState(lineItem.quantity)

  const orderingData = lineItem.orderingData
  const resolvedSource = lineItem.resolvedSource

  const availableDistributors = useMemo(() => {
    if (!orderingData) return []
    return orderingData
      .filter((d) => d.is_available)
      .map((d) => d.distributor)
      .filter((d): d is HardwareSupplierFilterKeyType => !!d)
  }, [orderingData])

  const [selectedSource, setSelectedSource] = useState<HardwareSupplierFilterKeyType | 'component' | undefined>(
    resolvedSource
  )

  // Sync selectedSource when resolvedSource changes (from hook), but don't overwrite manual choices
  useEffect(() => {
    if (resolvedSource === selectedSource) return
    if (isManualSource(selectedSource)) return
    setSelectedSource(resolvedSource)
    if (lineItem.selectedDistributor !== resolvedSource) {
      onSourceChange(lineItem.uuid, resolvedSource as HardwareSupplierFilterKeyType | undefined)
    }
  }, [resolvedSource, selectedSource, lineItem.selectedDistributor, lineItem.uuid, onSourceChange])

  useEffect(() => {
    setQuantity(lineItem.quantity)
  }, [lineItem.quantity])

  // Get display info based on selected source
  const displayData = useMemo(() => {
    return orderingData?.find((d: DistributorDataTypeV2) => d.distributor === selectedSource) || orderingData?.[0]
  }, [orderingData, selectedSource])

  const imageUrl = displayData?.image_url
  const description = getLineItemProductDisplayName(displayData, lineItem.componentTitle) || lineItem.code
  const price = isManualSource(selectedSource)
    ? undefined
    : (displayData?.price ?? displayData?.price_detail?.price_ex_vat)

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuantity = Number(e.target.value)
    setQuantity(newQuantity)
    onQuantityChange(lineItem.uuid, newQuantity)
  }

  const handleSourceChange = (source: HardwareSupplierFilterKeyType | undefined) => {
    setSelectedSource(source)
    onSourceChange(lineItem.uuid, source)
  }

  const getComponentCategory = () => {
    const categoryMap: Record<string, string> = {
      module: 'Panels',
      inverter: 'Inverter',
      battery: 'Battery',
      other: 'Other',
    }
    return categoryMap[lineItem.componentType || ''] || 'Other'
  }

  return (
    <StyledTableRow>
      <StyledTableCell>
        <ProductCell>
          {imageUrl ? (
            <ProductImage src={imageUrl} alt={lineItem.code} />
          ) : (
            <ProductImagePlaceholder>No img</ProductImagePlaceholder>
          )}
          <ProductInfo>
            <ProductDescription>{description}</ProductDescription>
            <ProductCode>{lineItem.code}</ProductCode>
          </ProductInfo>
        </ProductCell>
      </StyledTableCell>
      <StyledTableCell>{getComponentCategory()}</StyledTableCell>
      <StyledTableCell>
        <QuantityInput
          type="number"
          value={quantity}
          onChange={handleQuantityChange}
          variant="outlined"
          size="small"
          inputProps={{ min: 0 }}
        />
      </StyledTableCell>
      <StyledTableCell>
        <SourceSelectorCell>
          <SourceSelector
            value={selectedSource}
            onChange={handleSourceChange}
            availableDistributors={availableDistributors}
          />
        </SourceSelectorCell>
      </StyledTableCell>
      <StyledTableCell style={{ textAlign: 'right' }}>
        {price ? `${currencySymbol}${(price * quantity).toFixed(2)}` : '-'}
      </StyledTableCell>
    </StyledTableRow>
  )
})

type ShopItemsTableProps = {
  lineItems: LineItemWithResolvedSource[]
  onAddComponent?: (component: { [key: string]: any }) => void
  /** Optional title for the section (default: "Shop Items") */
  title?: string
  /** Optional subtitle (default: "Items added from the Shop (not associated with a project)") */
  subtitle?: string
  /** When true, hide the "Add item" row (e.g. for confirmed/cart items) */
  hideAddRow?: boolean
  /** When false, hide add-item UI (read-only mode) */
  allowEdit?: boolean
}

const ShopItemsTable = memo(
  ({
    lineItems,
    onAddComponent,
    title = 'Shop Items',
    subtitle,
    hideAddRow = false,
    allowEdit = true,
  }: ShopItemsTableProps) => {
    const theme = useTheme()
    const translate = useTranslate()
    const projectOrderPresenter = useProjectOrderPresenter()

    const [isAddItemActive, setIsAddItemActive] = useState(false)
    const [isOpenAddItemDialog, setIsOpenAddItemDialog] = useState(false)
    const [showSuggestion, setShowSuggestion] = useState(false)
    const [componentSelectorComponentType, setComponentSelectorComponentType] =
      useState<BomComponentSelectorType | null>(null)

    const headerSubtitle = subtitle ?? 'Items added from the Shop (not associated with a project)'

    const handleQuantityChange = useCallback(
      (uuid: string, quantity: number) => {
        projectOrderPresenter.updateLineItemQuantity({ uuid, quantity })
      },
      [projectOrderPresenter]
    )

    const handleSourceChange = useCallback(
      (uuid: string, source: HardwareSupplierFilterKeyType | undefined) => {
        projectOrderPresenter.updateLineItemDistributor({ uuid, distributor: source })
      },
      [projectOrderPresenter]
    )

    const handleAddItem = () => {
      if (!allowEdit) return
      setIsAddItemActive(true)
    }

    const handleSelectComponent = (value: { [key: string]: any }) => {
      if (onAddComponent) {
        onAddComponent({
          code: value.code,
          quantity: 1,
          componentType: value?.component_type || value?.componentType || value?.type,
          componentTypeV2: (typeof value.type === 'string'
            ? value.type
            : value.other_component_type) as ComponentTypesV2,
          source: value?.priceSource !== 'component' ? value?.priceSource : 'component',
        })
      }
      setIsAddItemActive(false)
      handleResetComponentSelectorComponentType()
    }

    const handleOpenAddItemDialog = () => {
      if (!allowEdit) return
      setIsOpenAddItemDialog(true)
      setShowSuggestion(false)
    }

    const handleCloseAddItemDialog = () => {
      setIsOpenAddItemDialog(false)
    }

    const handleSelectCategory = (category: BomComponentSelectorType) => {
      handleCloseAddItemDialog()
      setComponentSelectorComponentType(category)
    }

    const handleResetComponentSelectorComponentType = () => {
      setComponentSelectorComponentType(null)
    }

    return (
      <TableContainer>
        <TableHeader>
          <HeaderTitle>{title}</HeaderTitle>
          <HeaderSubtitle>{headerSubtitle}</HeaderSubtitle>
        </TableHeader>
        <StyledTable>
          <StyledTableHead>
            <tr>
              <th style={{ width: '35%' }}>Product</th>
              <th style={{ width: '15%' }}>Category</th>
              <th style={{ width: '15%' }}>Quantity</th>
              <th style={{ width: '20%' }}>Source</th>
              <th style={{ width: '15%', textAlign: 'right' }}>Total</th>
            </tr>
          </StyledTableHead>
          <tbody>
            {lineItems.map((lineItem) => (
              <ShopItemsTableRow
                key={lineItem.uuid}
                lineItem={lineItem}
                onQuantityChange={handleQuantityChange}
                onSourceChange={handleSourceChange}
              />
            ))}
            {allowEdit && isAddItemActive && (
              <StyledTableRow>
                <StyledTableCell colSpan={5}>
                  <SearchInputContainer>
                    <ProductImagePlaceholder>No img</ProductImagePlaceholder>
                    <div style={{ flex: 1 }}>
                      <HardwareSearchInputV3
                        onEnter={() => null}
                        onSelect={handleSelectComponent}
                        isShop3={true}
                        variant="outlined"
                        location="bom"
                        showSuggestion={showSuggestion}
                        setShowSuggestion={setShowSuggestion}
                        onBrowse={handleOpenAddItemDialog}
                      />
                    </div>
                  </SearchInputContainer>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </tbody>
        </StyledTable>
        {!hideAddRow && allowEdit && (
          <TableFooter>
            <StyledAddItemButton variant="outlined" onClick={handleAddItem}>
              <AddIcon color={theme.palette.grey[900]} height="20" width="20" />
              <Typography textVariant="body1SemiBold" colorHex={theme.palette.grey[900] as `#${string}`}>
                Add Item
              </Typography>
            </StyledAddItemButton>
          </TableFooter>
        )}

        {isOpenAddItemDialog && (
          <AddItemDialog
            handleClose={handleCloseAddItemDialog}
            handleSelectCategory={handleSelectCategory}
            title="Add Item"
            content="Select a component category to add to your order."
          />
        )}
        {componentSelectorComponentType && (
          <ComponentSelectorDialog
            handleClose={handleResetComponentSelectorComponentType}
            componentType={componentSelectorComponentType}
            handleSelect={handleSelectComponent}
            skipProjectPermissionCheck={true}
          />
        )}
      </TableContainer>
    )
  }
)

export default ShopItemsTable
